import type { RouteId } from "../../app/router/routes";
import type { Role } from "../../shared/types/prisma-enums";

/**
 * G1–G4's decisions, pure.
 *
 * 🔴 EVERY BRANCH WORTH TESTING LIVES HERE AND NOT IN A COMPONENT — the repo has
 * no DOM renderer (OD-60), and one of these screens changes a staff credential.
 */

/** The three settings screens, in the order `information-architecture.md` §1 lists them. */
export const SETTINGS_TABS = [
  { id: "settings-accessibility", key: "accessibility" },
  { id: "settings-camera", key: "camera" },
] as const satisfies readonly { id: RouteId; key: string }[];

export type SettingsTabKey = (typeof SETTINGS_TABS)[number]["key"];

/** A tab as the frame draws it. */
export interface SettingsTab {
  id: RouteId;
  key: SettingsTabKey;
}

/**
 * The settings tabs this principal actually has.
 *
 * 🔴 THE SCHOOL TIER GETS NOTHING AT ALL, AND THE REASON IS WHOSE DEVICE IT IS.
 * Accessibility (reduced motion, play mode) and Camera & privacy are settings
 * for the machine a CHILD plays on — the classroom laptop a teacher sets up
 * before a thirty-minute slot. An admin opens this product on an office desktop
 * to read figures and manage staff; a camera-permission panel there reports on a
 * webcam no child will ever sit in front of, and a reduced-motion switch applies
 * to a dashboard with no animation to reduce. Both were offered only because the
 * tab list was one hard-coded constant rather than a decision.
 *
 * 🔴 AND THE TWO TABS THAT WERE THEIRS ARE BOTH GONE. G1 — every adult's own
 * password and authenticator — has been removed, and so has E6: its recording
 * default, language, year and holiday calendar went with the screen, and its
 * notice board — which briefly outlived it as a "Broadcast" sidebar item — has
 * since been removed too. An empty list is the honest result: `settingsHomeFor` returns `null` for both school
 * roles so the shell draws no control into a frame with no tabs in it.
 */
export function settingsTabsFor(
  role: Role | "DEVICE" | null,
): readonly SettingsTab[] {
  /*
   * 🔴 A PRINCIPAL NOW HAS NO SETTINGS AT ALL, AND THAT IS THE HONEST RESULT
   * RATHER THAN AN OVERSIGHT. G1 was the one tab every adult shared, and with it
   * gone this tier has nothing left: E6 is `roles: ['ADMIN']` (S6 — a principal
   * is school read-only) and G3/G4 are the classroom machine's, which a
   * principal does not sit at. `settingsHomeFor` returns `null` for them so the
   * shell draws no control, rather than a control that opens an empty frame.
   */
  if (role === "ADMIN" || role === "PRINCIPAL") return [];
  return [
    { id: "settings-accessibility", key: "accessibility" },
    { id: "settings-camera", key: "camera" },
  ];
}

/**
 * The password floor an admin setting a teacher's password is held to.
 *
 * 🔴 A DELIBERATE SECOND COPY OF `InviteService.MIN_PASSWORD_LENGTH`, and the
 * same one `inviteModel.ts` holds. Both exist so somebody is told while the box
 * is under their cursor rather than after a round trip. ⚠️ If the server's floor
 * moves, both move with it.
 *
 * ⚠️ IT OUTLIVED THE SCREEN IT WAS WRITTEN FOR. This was G1's — the account
 * tab's own change-password form — and that tab is gone. `TeachersScreen` (E3)
 * still holds an admin to the same floor when they set a teacher's password, so
 * the constant stays here rather than moving: this file is where the number is
 * already imported from, and relocating it would edit a screen that has no
 * reason to change.
 */
export const MIN_PASSWORD_LENGTH = 10;

/* ─────────────────────────────────────────────────────────── G4 ── */

/**
 * What the camera panel can be, from the browser's own permission state.
 *
 * 🔴 `unsupported` IS NOT AN ERROR AND MUST NOT RENDER AS ONE. Invariant I4: an
 * unusable camera draws nothing on the child's stage and the round routes
 * silently into tap mode. This screen is adult chrome, so it MAY say so — but
 * the wording has to be "this laptop has no camera", not "something went wrong".
 */
export type CameraState = "granted" | "denied" | "prompt" | "unsupported";

/**
 * ⚠️ `Permissions.query` IS NOT UNIVERSAL, and the absence of the API is not the
 * absence of a camera. Firefox has never supported `{ name: 'camera' }`, so a
 * screen that treated a rejected query as "denied" would tell a teacher on
 * Firefox their camera was blocked when it was not — and the fix they would
 * reach for (browser settings) would not help.
 */
export function describeCameraState(
  permission: PermissionState | null,
  hasMediaDevices: boolean,
): CameraState {
  if (!hasMediaDevices) return "unsupported";
  if (permission === null) return "prompt";
  return permission === "granted"
    ? "granted"
    : permission === "denied"
      ? "denied"
      : "prompt";
}
